import * as THREE from 'three'
import React, { Suspense, useEffect, useState, useRef } from 'react'
import { useFrame, useLoader } from '@react-three/fiber'
import { PerspectiveCamera, Environment, MeshDistortMaterial, ContactShadows } from '@react-three/drei'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader'
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader'
import { useSpring } from '@react-spring/core'
import { a } from '@react-spring/three'
import { useTheme } from './ThemeContext'

// React-spring animated components
const AnimatedMaterial = a(MeshDistortMaterial)
const AnimatedGroup = a.group

// Fall back to a basic shape if model loading fails
function FallbackShape(props) {
  return (
    <mesh {...props}>
      <sphereGeometry args={[1, 32, 32]} />
      <AnimatedMaterial 
        color={props.color} 
        envMapIntensity={props.envMapIntensity} 
        clearcoat={props.clearcoat} 
        clearcoatRoughness={props.clearcoatRoughness} 
        metalness={props.metalness} 
        roughness={props.roughness}
      />
    </mesh>
  )
}

// Load the model with error handling
function Model({ color, envMapIntensity, clearcoat, clearcoatRoughness, metalness, roughness, onLoadComplete, ...props }) {
  const [hasError, setHasError] = useState(false);
  const isMounted = useRef(true);
  
  // Load the MTL file first, then the OBJ file
  const materials = useLoader(MTLLoader, '/3d/LK.mtl');
  const obj = useLoader(OBJLoader, '/3d/LK.obj', loader => {
    materials.preload();
    loader.setMaterials(materials);
  });
  
  // Check for loading errors and cleanup
  useEffect(() => {
    if (!isMounted.current) return;
    if (!obj) {
      setHasError(true);
    } else if (onLoadComplete) {
      // Signal that the model has loaded
      onLoadComplete();
    }
    
    return () => {
      isMounted.current = false;
    };
  }, [obj, onLoadComplete]);
  
  // Return fallback if loading failed
  if (hasError || !obj) {
    return <FallbackShape {...props} color={color} envMapIntensity={envMapIntensity} 
      clearcoat={clearcoat} clearcoatRoughness={clearcoatRoughness} 
      metalness={metalness} roughness={roughness} />
  }
  
  // Clone the object to avoid modifying the original
  const modelClone = obj.clone();
  
  // Force chrome liquid materials on all meshes
  modelClone.traverse((child) => {
    if (child.isMesh) {
      // Replace with enhanced dark black chrome material
      child.material = new THREE.MeshPhysicalMaterial({
        color: color || 0x111111,      // Dark black chrome color
        metalness: 1.0,                // Maximum metalness for chrome
        roughness: 0.05,               // Even smoother for higher reflectivity
        envMapIntensity: 1.0,          // Strong environment reflections (reduced a bit for darker appearance)
        clearcoat: 1.0,                // Maximum clearcoat for liquid look
        clearcoatRoughness: 0.04,      // Ultra smooth clearcoat
        reflectivity: 1.0,             // Maximum reflectivity
        ior: 3.0,                      // Higher index of refraction for stronger reflections
      });
      
      // Ensure proper shadow casting
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
  
  return (
    <group {...props}>
      <primitive object={modelClone} />
    </group>
  )
}

export default function Scene({ setBg, onThemeChange, onLoadComplete }) {
  const model = useRef()
  const light = useRef()
  const [mode, setMode] = useState(false)
  const [down, setDown] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [fov, setFov] = useState(60)
  const [modelLoaded, setModelLoaded] = useState(false)
  
  // Use the ThemeContext instead of local state
  const { isDarkMode, toggleDarkMode } = useTheme()

  // Handle model loading completion
  const handleModelLoad = () => {
    setModelLoaded(true);
    if (onLoadComplete) {
      onLoadComplete();
    }
  };

  // Make the model float and follow the mouse
  useFrame((state) => {
    light.current.position.x = state.mouse.x * 20
    light.current.position.y = state.mouse.y * 20
    if (model.current) {
      // Add smoothing factor to reduce jitter
      const smoothingFactor = 0.1;
      
      // Calculate target position with reduced edge sensitivity
      const targetX = hovered 
        ? Math.min(Math.max(state.mouse.x / 3, -0.3), 0.3) // Limit range and reduce sensitivity
        : 0;
      const targetY = Math.sin(state.clock.elapsedTime / 1.5) / 6 + 
        (hovered ? Math.min(Math.max(state.mouse.y / 3, -0.3), 0.3) : 0); // Limit range
      
      // Apply smoothing to position updates
      model.current.position.x = THREE.MathUtils.lerp(
        model.current.position.x, 
        targetX, 
        smoothingFactor
      );
      model.current.position.y = THREE.MathUtils.lerp(
        model.current.position.y,
        targetY,
        smoothingFactor
      );
      
      // Add a gentler rotation with smoothing
      const targetRotationY = (hovered ? state.mouse.x * 0.3 : 0) + 
        state.clock.elapsedTime * 0.05;
      
      model.current.rotation.y = THREE.MathUtils.lerp(
        model.current.rotation.y,
        targetRotationY,
        smoothingFactor * 0.8 // Even smoother rotation
      );
    }
  })

  // Update the useSpring hook to have a more gradual transition
  const [{ wobble, coat, color, ambient, env, rough, metal }] = useSpring(
    {
      // Reduce the range between states for a more controlled effect
      wobble: down ? 2.8 : hovered ? 3.05 : 3,
      
      coat: mode && !hovered ? 1 : 1,
      ambient: mode && !hovered ? 0.5 : 0.3,
      env: mode && !hovered ? 4.0 : 3.5,
      color: hovered ? '#222222' : isDarkMode ? '#111111' : '#000000',
      rough: mode && !hovered ? 0.05 : 0.08,
      metal: mode && !hovered ? 1.0 : 1.0,
      
      config: (n) => {
        // Refined easing for wobble transitions with less extreme bounce
        if (n === 'wobble') {
          return { 
            mass: 2.5,           // Balanced mass for all states
            tension: 80,         // Lower tension for smoother transitions
            friction: 18,        // Higher friction to prevent oscillation
            clamp: false,        // Allow natural physics
            velocity: 0          // No initial velocity to prevent jumps
          }
        }
        return { 
          tension: 80, 
          friction: 20,
          delay: 50  // Small delay to make transitions feel more natural
        }
      }
    },
    [isDarkMode, hovered, down]
  )

  // Function to toggle the theme - now uses ThemeContext
  const toggleTheme = () => {
    console.log('Scene: Toggling theme');
    toggleDarkMode(); // Use the context's toggle function
    
    // Call setBg with the appropriate theme colors
    if (!isDarkMode) { // Note: we check the current value before it changes
      // Dark theme
      setBg({
        background: '#121212',
        color: '#ffffff',
        fill: '#ffffff'
      });
      console.log('Scene: Switching to dark mode');
    } else {
      // Light theme
      setBg({
        background: '#efefef',
        color: '#000000',
        fill: '#000000'
      });
      console.log('Scene: Switching to light mode');
    }
    
    // If onThemeChange callback exists, call it with the new theme value
    if (onThemeChange) {
      onThemeChange(!isDarkMode);
    }
  };

  // Initialize theme on component mount
  useEffect(() => {
    // Set initial theme based on isDarkMode from context
    if (isDarkMode) {
      setBg({
        background: '#121212',
        color: '#ffffff',
        fill: '#ffffff'
      });
      console.log('Scene: Initial theme set to dark mode');
    } else {
      setBg({
        background: '#efefef',
        color: '#000000',
        fill: '#000000'
      });
      console.log('Scene: Initial theme set to light mode');
    }
    
    // Sync the local mode state with isDarkMode from context
    setMode(isDarkMode);
  }, [isDarkMode, setBg]);

  // Add resize listener to adjust FOV based on screen width
  useEffect(() => {
    // Function to update FOV based on window width
    const updateFov = () => {
      if (window.innerWidth < 768) {
        setFov(60);
      } else {
        setFov(60);
      }
    };
    
    // Set initial FOV
    updateFov();
    
    // Add event listener
    window.addEventListener('resize', updateFov);
    
    // Clean up
    return () => window.removeEventListener('resize', updateFov);
  }, []);

  return (
    <>
      <PerspectiveCamera makeDefault position={[0, 0, 14]} fov={fov}>
        <a.ambientLight intensity={ambient} />
        <a.pointLight ref={light} position-z={-15} intensity={3.0} color="#FFFFFF" />
        <directionalLight position={[5, 5, 5]} intensity={1.5} />
        <pointLight position={[-5, 3, 0]} intensity={1.5} color="#F8F8FF" />
        <pointLight position={[0, -5, 0]} intensity={1.2} color="#E0E0FF" />
        <pointLight position={[0, 0, -10]} intensity={1.0} color="#FFFFFF" />
      </PerspectiveCamera>
      <Suspense fallback={null}>
        <AnimatedGroup
          ref={model}
          scale={wobble.to(w => w * 1.5)}
          onPointerOver={() => setHovered(true)}
          onPointerOut={() => setHovered(false)}
          onPointerDown={() => setDown(true)}
          onPointerUp={() => {
            setDown(false)
            // Toggle mode between dark and bright
            setMode(!mode)
            toggleTheme()
          }}>
          <Model 
            color={color} 
            envMapIntensity={env} 
            clearcoat={coat} 
            clearcoatRoughness={0} 
            metalness={metal} 
            roughness={rough}
            onLoadComplete={handleModelLoad}
          />
        </AnimatedGroup>
        <Environment preset="forest" /* or try: "city", "dawn", "sunset" */ />
        <ContactShadows
          rotation={[Math.PI / 2, 0, 0]}
          position={[0, -5, 0]}
          opacity={isDarkMode ? 0.9 : 0.4}
          width={5}
          height={4}
          blur={2.1}
          far={5}
        />
      </Suspense>
    </>
  )
}

// Preload the OBJ and MTL
const mtlLoader = new MTLLoader();
const objLoader = new OBJLoader();

mtlLoader.load('/3d/LK.mtl', (materials) => {
  materials.preload();
  objLoader.setMaterials(materials);
  objLoader.load('/3d/LK.obj', () => console.log('OBJ preloaded'));
});
